import "styles/App.scss";

import { useIsFetching, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminAccount } from "components/AdminAccount";
import { AuthCallback } from "components/AuthCallback";
import { Header } from "components/Header";
import { PageWrapper } from "components/PageWrapper";
import { AccountDetailsPage } from "pages/AccountDetailsPage";
import { AccountPage } from "pages/AccountPage";
import { AddressDetailsPage } from "pages/AddressDetailsPage";
import { BillingDetailsPage } from "pages/BillingDetailsPage";
import { BillListPage } from "pages/BillListPage";
import { BillPage } from "pages/BillPage";
import { BillSmootherInformationPage } from "pages/BillSmootherInformationPage";
import { BillSmootherPage } from "pages/BillSmootherPage";
import { BillSmootherSettingsPage } from "pages/BillSmootherSettingsPage";
import { CloseAccountPage } from "pages/CloseAccountPage";
import { CurrentPlanPage } from "pages/CurrentPlanPage";
import { HelpPage } from "pages/HelpPage";
import { HomePage } from "pages/HomePage/HomePage";
import { LoadingPage } from "pages/LoadingPage";
import { MovingHousePage } from "pages/MovingHousePage";
import { NewPaymentMethodPage } from "pages/NewPaymentMethodPage";
import { SplashPage } from "pages/SplashPage/SplashPage";
import { UpdatePasswordPage } from "pages/UpdatePasswordPage";
import { UsagePage } from "pages/UsagePage";
import { getBillingEntity } from "queries/billing";
import { getContracts, getPlans } from "queries/contract_manager";
import { getAccountDetails, getAccountInfo } from "queries/customer";
import { getUserDetails } from "queries/identity";
import { getRateCards } from "queries/pricing";
import { getRatingPreferences } from "queries/rating";
import React, { useEffect, useMemo, useState } from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { setAccountDatas } from "reducers/accountDatas";
import {
  setAccountDetails,
  setCurrentAccountStatus,
} from "reducers/accountDetails";
import { setAnnouncementBanner } from "reducers/announcementBanner";
import {
  setAvailablePlanDescriptions,
  setAvailablePlans,
  setAvailablePlansLoadingStatus,
} from "reducers/availablePlans";
import { setBillingEntityData } from "reducers/billingEntityData";
import {
  setCurrentAccountLocationClassification,
  setCurrentAccountUsageType,
  setNativeRenderMode,
} from "reducers/currentAccount";
import {
  setCurrentProduct,
  setCurrentProductErrorStatus,
} from "reducers/currentProduct";
import { setRateCards, setRateCardsLoadingStatus } from "reducers/rateCards";
import { useAppDispatch, useAppSelector } from "reduxUtils/hook";
import { RATE_CARD_QUERY_TYPES, RENDER_MODE } from "utils/constants";
import {
  findLocationClassification,
  findUsageType,
} from "utils/findRatingPreferences";
import { jwtClient } from "utils/jwtClient";
import {
  filteredLinkContent,
  sendAnalyticsEvent,
} from "utils/sendAnalyticsEvent";

import NewDashboardFeedback from "./components/AnnouncementBanner/static/Welcome-to-new-dashboard-feedback.json";

const MAIN_PAGES = [
  "/",
  "/bills",
  "/usage",
  "/account",
  "/account/billing",
  "/account/billing/new-payment-method",
  "/account/details",
  "/account/address",
  "/account/address/update",
  "/account/close-account",
  "/account/details/password",
  "/bills/bill-smoother",
  "/account/plan",
  "/account/help",
];

// Sets the value for the root route
const RootRouteWrapper = React.memo<{
  isAuthenticated: boolean;
  refreshToken?: string;
  nativeRenderMode: boolean;
}>(({ isAuthenticated, refreshToken, nativeRenderMode }) => {
  if (isAuthenticated) {
    return <HomePage />;
  }
  // If a user has a refresh token stored in localStorage,
  // render a LoadingPage instead of the SplashPage, while identity finishes authenticating.
  // If there is no refreshToken stored, just show the SplashPage
  return refreshToken ? (
    <LoadingPage />
  ) : (
    <SplashPage
      login={() => jwtClient.authenticateWithIdentity()}
      isAuthenticated={isAuthenticated}
      nativeRenderMode={nativeRenderMode}
    />
  );
});

export const App = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const currentAccount = useAppSelector((store) => store.currentAccount);
  const queriesInProgress = useIsFetching({
    predicate: (query) => query.state.status === "loading",
  });
  const localNativeRenderMode =
    window?.localStorage?.getItem("nativeRenderMode");
  const [nativeRenderMode] = useState(
    Boolean(localNativeRenderMode) ||
      searchParams.get("render_mode") === RENDER_MODE.native,
  );
  const [isAuthenticated, setIsAuthenticated] = useState(nativeRenderMode);
  const [authExpired, setAuthExpired] = useState(null);
  const refreshToken = window?.localStorage?.getItem("refreshToken");
  const accountOverrideNumber = window.sessionStorage.getItem(
    "accountOverrideNumber",
  );

  jwtClient.setOnAuthenticationChange(setIsAuthenticated);
  jwtClient.authenticatedInApp(nativeRenderMode);
  jwtClient.setNavigate(navigate);
  jwtClient.setDispatch(dispatch);
  jwtClient.setAuthExpired(setAuthExpired);

  useEffect(() => {
    const attemptLogin = async () => {
      await jwtClient.attemptLogin(refreshToken, navigate, pathname + search);
    };
    if (refreshToken) {
      attemptLogin();
    }

    if (nativeRenderMode && !Boolean(localNativeRenderMode)) {
      window?.localStorage?.setItem(
        "nativeRenderMode",
        String(nativeRenderMode),
      );
    }

    window["setNativeJwt"] = (token) => {
      jwtClient.setNativeJwt(token);
      accountDataRefetch();
    };

    window["webNavigate"] = (path) => {
      navigate(path);
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(
        setAnnouncementBanner({
          type: NewDashboardFeedback.type,
          modifier: NewDashboardFeedback.modifier ?? null,
          disableNativeMode: NewDashboardFeedback.disableNativeMode ?? false,
          name: "AnnouncementBanner",
          params: {
            heading: NewDashboardFeedback.heading,
            bodyCopy: NewDashboardFeedback.bodyCopy,
          },
          additionalContent:
            NewDashboardFeedback.links &&
            filteredLinkContent(NewDashboardFeedback.links),
        }),
      );
    }
  }, [isAuthenticated]);

  useMemo(() => {
    window["jwtExpired"] = () => {
      if (Boolean(queriesInProgress) || authExpired === null) return null;
      return authExpired;
    };
  }, [authExpired, queriesInProgress]);

  useEffect(() => {
    if (nativeRenderMode !== currentAccount.nativeRenderMode) {
      dispatch(
        setNativeRenderMode({
          nativeRenderMode: nativeRenderMode,
        }),
      );
    }
  }, [dispatch, nativeRenderMode]);

  useEffect(() => {
    if (!Boolean(currentAccount.icpNumber)) return;
    sendAnalyticsEvent("page_view", { icpNumber: currentAccount.icpNumber });
  }, [pathname, currentAccount.icpNumber]);

  const shouldDisplayHeader =
    isAuthenticated && !currentAccount.nativeRenderMode;
  const {
    isLoading: isAccountLoading,
    data: accountData,
    refetch: accountDataRefetch,
  } = useQuery(
    ["accountinfo", { jwtClient, accountOverrideNumber }],
    getAccountInfo,
    { refetchOnWindowFocus: true, enabled: isAuthenticated },
  );

  useEffect(() => {
    if (accountData?.length) {
      dispatch(setAccountDatas({ accountDatas: accountData }));
    }
  }, [accountData, dispatch]);

  const { data: contractData, error: isContractDataError } = useQuery(
    ["contractinfo", { supplyNodeRef: currentAccount.supplyNodeRef }],
    () =>
      getContracts({
        customerNumber: currentAccount.customerNumber,
        supplyNodeRef: currentAccount.supplyNodeRef,
        jwtClient,
      }),
    {
      enabled: Boolean(currentAccount.supplyNodeRef),
    },
  );

  useEffect(() => {
    if (contractData) {
      dispatch(setCurrentProduct({ contractData }));
    }
    dispatch(
      setCurrentProductErrorStatus({
        error: isContractDataError,
      }),
    );
  }, [contractData, isContractDataError, dispatch]);

  const { data: billingEntityData } = useQuery(
    [
      "billingEntity",
      { customerNumber: currentAccount.customerNumber, jwtClient },
    ],
    getBillingEntity,
    {
      enabled: !!currentAccount.customerNumber,
    },
  );

  useEffect(() => {
    if (billingEntityData) {
      dispatch(setBillingEntityData({ billingEntityData }));
    }
  }, [billingEntityData, dispatch]);

  const { data: accountDetails } = useQuery(
    ["accountinfo", { accountNumber: currentAccount.accountNumber, jwtClient }],
    getAccountDetails,
    {
      enabled: !!currentAccount.accountNumber,
    },
  );

  useEffect(() => {
    if (accountDetails) {
      dispatch(
        setCurrentAccountStatus({
          vulnerabilityState: accountDetails.vulnerabilityState,
        }),
      );
    }
  }, [accountDetails, dispatch]);

  const { data: userDetails } = useQuery(
    ["userDetails", { userId: accountDetails?.userSub, jwtClient }],
    getUserDetails,
    {
      enabled: !!accountDetails?.userSub,
    },
  );

  useEffect(() => {
    if (userDetails) {
      const { paymentMethods, primaryContact, userSub, currentICPAddress } =
        accountDetails;
      dispatch(
        setAccountDetails({
          ...userDetails,
          currentICPAddress,
          paymentMethods,
          primaryContact,
          userSub,
        }),
      );
    }
  }, [userDetails]);

  const { data: ratingPreferences } = useQuery(
    [
      "ratingReferences",
      { supplyNodeRef: currentAccount.supplyNodeRef, jwtClient },
    ],
    getRatingPreferences,
    {
      enabled: Boolean(currentAccount.supplyNodeRef),
    },
  );

  const usageType = findUsageType(ratingPreferences);

  const locationClassification = findLocationClassification(ratingPreferences);

  useEffect(() => {
    if (ratingPreferences) {
      dispatch(setCurrentAccountUsageType({ usageType }));
      dispatch(
        setCurrentAccountLocationClassification({ locationClassification }),
      );
    }
  }, [ratingPreferences]);

  const {
    data: rateCards,
    isLoading: isRateCardLoading,
    isError: isRateCardError,
  } = useQuery(
    [
      "rateCard",
      {
        icpNumber: currentAccount.icpNumber,
        jwtClient,
        generationPriceContract: contractData?.contractName,
        type: RATE_CARD_QUERY_TYPES.rateCards,
        usageType: currentAccount.usageType,
        locationClassification: currentAccount.locationClassification,
      },
    ],
    getRateCards,
    {
      enabled: Boolean(currentAccount.icpNumber) && Boolean(contractData),
    },
  );

  useEffect(() => {
    if (rateCards) {
      dispatch(setRateCards({ rateCards: rateCards as { data: RateCard[] } }));
    }
    dispatch(
      setRateCardsLoadingStatus({
        loading: isRateCardLoading,
        error: isRateCardError,
      }),
    );
  }, [rateCards, isRateCardLoading, isRateCardError]);

  const {
    data: availablePlans,
    isLoading: isAvailablePlansLoading,
    isError: isAvailablePlansError,
  } = useQuery(
    [
      "availablePlans",
      {
        jwtClient,
        supplyNodeRef: currentAccount.supplyNodeRef,
        type: RATE_CARD_QUERY_TYPES.availablePlans,
        usageType: currentAccount.usageType,
        locationClassification: currentAccount.locationClassification,
      },
    ],
    getRateCards,
    {
      enabled: !!currentAccount.supplyNodeRef,
    },
  );

  useEffect(() => {
    if (availablePlans) {
      dispatch(
        setAvailablePlans({ availablePlans } as {
          availablePlans: AvailablePlan[];
        }),
      );
    }
    dispatch(
      setAvailablePlansLoadingStatus({
        loading: isAvailablePlansLoading,
        error: isAvailablePlansError,
      }),
    );
  }, [availablePlans, isAvailablePlansLoading, isAvailablePlansError]);

  const { data: planDescriptions } = useQuery(
    ["planinfo", { supplyNodeRef: currentAccount.supplyNodeRef }],
    () =>
      getPlans({
        customerNumber: currentAccount.customerNumber,
        supplyNodeRef: currentAccount.supplyNodeRef,
        jwtClient,
      }),
    {
      enabled: !!availablePlans,
    },
  );

  useEffect(() => {
    if (planDescriptions) {
      dispatch(setAvailablePlanDescriptions({ planDescriptions }));
    }
  }, [planDescriptions]);

  useEffect(() => {
    !isAuthenticated && queryClient.clear();
  }, [isAuthenticated]);

  if (isAccountLoading && isAuthenticated) return <LoadingPage />;

  return (
    <>
      {shouldDisplayHeader && (
        <Header
          handleLogout={() => {
            window.sessionStorage.removeItem("accountOverrideNumber");
            jwtClient.clearJwt();
          }}
        />
      )}
      <Routes>
        <Route
          path="/welcome"
          element={
            <SplashPage
              login={() => jwtClient.authenticateWithIdentity()}
              isAuthenticated={isAuthenticated}
              nativeRenderMode={nativeRenderMode}
            />
          }
        />
        <Route
          element={
            <PageWrapper
              isAuthenticated={isAuthenticated}
              userIsOnMainPage={MAIN_PAGES.includes(pathname)}
              nativeRenderMode={nativeRenderMode}
            >
              <Outlet />
            </PageWrapper>
          }
        >
          <Route
            path="/"
            element={
              <RootRouteWrapper
                isAuthenticated={isAuthenticated}
                refreshToken={refreshToken}
                nativeRenderMode={nativeRenderMode}
              />
            }
          />
          <Route
            path="auth/callback"
            element={<AuthCallback setIsAuthenticated={setIsAuthenticated} />}
          />
          <Route path="admin/:accountNumber" element={<AdminAccount />} />
          {isAuthenticated && (
            <>
              <Route path="bills">
                <Route index element={currentAccount && <BillListPage />} />
                <Route path="bill-smoother">
                  <Route index element={<BillSmootherPage />} />
                  <Route
                    path="settings"
                    element={<BillSmootherSettingsPage />}
                  />
                  <Route
                    path="more-information"
                    element={<BillSmootherInformationPage />}
                  />
                </Route>
                <Route path=":invoiceNumber" element={<BillPage />} />
              </Route>
              <Route path="choice" element={<h1>Choice</h1>} />
              <Route path="usage/:periodDuration?" element={<UsagePage />} />
              <Route path="account">
                <Route index element={<AccountPage />} />
                <Route path="address">
                  <Route index element={<AddressDetailsPage />} />
                  <Route path="update" element={<MovingHousePage />} />
                </Route>
                <Route path="billing">
                  <Route index element={<BillingDetailsPage />} />
                  <Route
                    path="new-payment-method"
                    element={<NewPaymentMethodPage />}
                  />
                </Route>
                <Route path="details">
                  <Route index element={<AccountDetailsPage />} />
                  <Route path="password" element={<UpdatePasswordPage />} />
                </Route>
                <Route path="close-account" element={<CloseAccountPage />} />
                <Route path="plan" element={<CurrentPlanPage />} />
                <Route path="help" element={<HelpPage />} />
              </Route>
              <Route path="settings" element={<h1>Settings</h1>} />
            </>
          )}
          <Route path="*" element={<Navigate to="/" />} />
        </Route>
      </Routes>
    </>
  );
};
